<template>
  <transition name="menu">
    <div
      v-if="open"
      class="z-50 font-body bg-n-900/80 sm:backdrop-blur-sm absolute top-0 left-0 w-screen h-screen flex justify-end overflow-hidden"
      @click="($emit('close'), (showSettings = false))"
    >
      <div
        class="sidebar bg-n-800 text-n-0 w-full sm:w-80 h-full flex flex-col overflow-hidden pb-safe"
        @click.stop
      >
        <div class="bg-n-900 pt-safe">
          <div class="flex justify-end px-4 items-center h-14">
            <l-button
              size="medium"
              icon="close"
              type="text"
              color="white"
              @click="($emit('close'), (showSettings = false))"
            />
          </div>
        </div>
        <div class="flex-1 relative">
          <transition :name="showSettings ? 'menu-left' : 'menu-right'">
            <div v-if="!showSettings" class="absolute inset-0 w-full flex flex-col">
              <div class="bg-n-900 px-5 pb-3">
                <div class="mb-2 font-heading text-4">
                  {{ currentEmployee?.name }}
                </div>
                <router-link v-slot="{ navigate }" :to="{ name: 'employees' }">
                  <button
                    class="text-v-300"
                    @click="
                      () => {
                        emit('close')
                        navigate()
                      }
                    "
                  >
                    {{ t('sidemenu.change-employee') }}
                  </button>
                </router-link>
              </div>
              <div class="flex-1 flex flex-col px-5 overflow-hidden">
                <div
                  v-if="shipmentProvidersEnabled.length > 0"
                  class="border-b border-n-700 flex-none mt-5"
                >
                  <l-option-selector
                    v-if="shouldShowManualShipmentSwitch"
                    v-model="isManualShipment"
                    :options="shipmentOptions"
                    class="w-full mb-4"
                  />
                  <div v-if="shipmentProvidersEnabled.length > 1" class="mb-5">
                    <l-select
                      v-model="selectedShipmentProvider"
                      :options="shipmentProvidersEnabled"
                    />
                  </div>
                  <div
                    v-if="shouldShowOwnFleetSwitch"
                    class="flex justify-between items-center mb-5"
                  >
                    <div>{{ t('sidemenu.use-own-fleet') }}</div>
                    <l-switch
                      :model-value="ownFleetEnabled"
                      @update:model-value="updateOwnFleet($event as boolean)"
                    />
                  </div>
                </div>

                <div class="overflow-y-scroll flex-1">
                  <div
                    v-if="hasDeliveries && !isBookingOnly"
                    class="py-3 flex items-center gap-2 cursor-pointer"
                    @click="closeDelivery()"
                  >
                    <l-icon name="store-closed" />
                    {{ t('sidemenu.close-delivery') }}
                  </div>

                  <div
                    v-if="hasReservationSubscription && bookingsBrand"
                    class="py-3 flex items-center gap-2 cursor-pointer"
                    @click="closeBookings()"
                  >
                    <l-icon :name="bookingsBrand.closedUntil ? 'calendar' : 'calendar-plus'" />
                    {{
                      bookingsBrand.closedUntil
                        ? t('sidemenu.open-bookings')
                        : t('sidemenu.close-bookings')
                    }}
                  </div>

                  <hr
                    v-if="hasDeliveries || (hasReservationSubscription && bookingsBrand)"
                    class="border-n-700 flex-none"
                  />

                  <div
                    v-if="(selectedCashTill || currentEmployee?.tillId) && !isBookingOnly"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="routerPrompt('TILL_MANAGER', 'payInPayOut')"
                  >
                    <l-icon name="double-arrow-sides" />
                    {{ t('sidemenu.pay-in-pay-out') }}
                  </div>

                  <div
                    v-if="startedShifts.length > 0 && !isBookingOnly"
                    class="flex items-center cursor-pointer gap-2 py-3"
                    @click="closeDay()"
                  >
                    <l-icon name="inactive-2" />
                    {{ t('sidemenu.close-day') }}
                  </div>

                  <hr
                    v-if="selectedCashTill || currentEmployee?.tillId || startedShifts.length > 0"
                    class="border-n-700 flex-none"
                  />

                  <div
                    v-if="!isBookingOnly"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="routerPrompt('REAL_TIME_REPORTER', 'realTimeReport')"
                  >
                    <l-icon name="columns" />
                    {{ t('sidemenu.real-time-report') }}
                  </div>

                  <router-link
                    v-if="Object.keys(couriers).length > 0"
                    :to="{ name: 'couriersReport' }"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="$emit('close')"
                  >
                    <l-icon name="bike" />
                    {{ t('sidemenu.couriers-report') }}
                  </router-link>

                  <div
                    v-if="!isBookingOnly"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="routerPrompt('PRODUCT_MANAGER', 'products')"
                  >
                    <l-icon name="product" />
                    {{ t('sidemenu.products') }}
                  </div>

                  <hr class="border-n-700 flex-none" />

                  <router-link
                    v-if="!isBookingOnly"
                    :to="{ name: 'hardwareSelector' }"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="$emit('close')"
                  >
                    <l-icon name="mobile" />
                    {{ t('sidemenu.hardware-management') }}
                  </router-link>

                  <div
                    v-if="!isBookingOnly"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="router.push({ name: 'printers' })"
                  >
                    <l-icon name="printer" />
                    {{ t('sidemenu.printers') }}
                  </div>

                  <div
                    v-if="hasCashlogy"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="openBackoffice()"
                  >
                    <l-icon name="cash" />
                    {{ t('sidemenu.open-cashlogy') }}
                  </div>

                  <div
                    v-if="hasFeedback"
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="openFeedback()"
                  >
                    <l-icon name="chat" />
                    {{ t('sidemenu.feedback') }}
                  </div>

                  <div
                    class="py-3 flex items-center cursor-pointer gap-2"
                    @click="showSettings = true"
                  >
                    <l-icon name="settings" />
                    {{ t('configuration.settings') }}
                  </div>
                  <div v-if="isElectron" class="py-3 gap-3 flex flex-col border-t border-n-700">
                    <div class="flex items-center cursor-pointer gap-2" @click="minimizeApp()">
                      <l-icon name="minus" />
                      {{ t('sidemenu.minimize-screen') }}
                    </div>
                    <div class="flex items-center cursor-pointer gap-2" @click="closeApp()">
                      <l-icon name="power" />
                      {{ t('sidemenu.close-application') }}
                    </div>
                  </div>
                </div>
                <div class="flex-none flex-col w-full mb-2 bg-n-800">
                  <l-button
                    v-if="!isMobile && !isBookingOnly"
                    class="mb-2 w-full"
                    size="small"
                    @click="openCashDrawer()"
                  >
                    {{ t('topbar.open-register') }}
                  </l-button>
                  <router-link
                    :to="{ name: 'about' }"
                    class="flex flex-row items-center justify-center"
                  >
                    <div class="text-xs">{{ t('sidemenu.version') }} {{ appVersion }}</div>
                    <l-icon class="px-1" name="question-circle" />
                  </router-link>
                </div>
              </div>
            </div>
            <settings
              v-else-if="showSettings"
              @back="showSettings = false"
              class="absolute inset-0"
            />
          </transition>
        </div>
      </div>
    </div>
  </transition>
  <closing-time-modal
    v-if="showBookingsModal"
    @close="showBookingsModal = false"
    @confirm="handleCloseBookings"
  />
</template>

<script setup lang="ts">
import { computedAsync } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { productFruits } from 'product-fruits'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import {
  LButton,
  LIcon,
  LOptionSelector,
  LSelect,
  LSwitch,
  useTracker
} from '@last/core-ui/paprika'

import CashMachine from '@/integrations/cashmachine/cashmachine'
import Printer from '@/integrations/printer/printer'
import ClosingTimeModal from '@/modules/delivery/ClosingTimeModal.vue'
import { managerConfirmation } from '@/plugins/managerConfirmation'
import { useToast } from '@/plugins/toast/toast'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useCouriersStore } from '@/store/couriers'
import { useDeliveryCompaniesStore } from '@/store/deliveryCompanies'
import { useTillStore } from '@/store/till'
import type { ClosingTime } from '@/types/virtualBrands'
import { isElectron, isMobile } from '@/utils/device-type'
import { closeApp, minimizeApp } from '@/utils/electron'

import Settings from './Settings.vue'

type Props = {
  open: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const { t, locale } = useI18n()
const toast = useToast()
const tracker = useTracker()

const authStore = useAuthStore()
const configStore = useConfigStore()
const couriersStore = useCouriersStore()
const tillStore = useTillStore()
const deliveryStore = useDeliveryCompaniesStore()

const { updateShipmentProvider, updateManualShipment } = configStore
const { closeDeliveries, openDeliveries } = deliveryStore

const { currentEmployee, hasReservationSubscription, isBookingOnly, locationId } =
  storeToRefs(authStore)
const { shipmentProviders, config, appVersion, device } = storeToRefs(configStore)

const { couriers } = storeToRefs(couriersStore)
const { selectedCashTill, startedShifts } = storeToRefs(tillStore)
const { hasDeliveries, bookingsBrand } = storeToRefs(deliveryStore)

const showSettings = ref(false)
const showBookingsModal = ref(false)

const hasFeedback = computedAsync(() => tracker.hasFeatureFlag('pos-feedback'), false)

const hasCashlogy = computed(() => CashMachine.method === 'cashlogy')

const shipmentOptions = computed(() => [
  { label: t('sidemenu.automatic-shipment'), value: false },
  { label: t('sidemenu.manual-shipment'), value: true }
])

const shipmentProvidersEnabled = computed<{ label: string; value: string }[]>(() => {
  if (!shipmentProviders.value || !shipmentProviders.value.providers) return []
  return shipmentProviders.value.providers.reduce(
    (res: { label: string; value: string }[], provider) => {
      if (Object.keys(provider).length > 1) {
        res.push({ label: provider.name, value: provider.name })
      }
      return res
    },
    []
  )
})

const selectedShipmentProvider = computed({
  get: () => {
    if (!shipmentProviders.value) return null
    return shipmentProviders.value.selected
  },
  set: (value: string | null) =>
    updateShipmentProvider({
      provider: value,
      ownFleetEnabled: ownFleetEnabled.value
    })
})

const shouldShowManualShipmentSwitch = computed<boolean>(() => {
  return !!selectedShipmentProvider.value && config.value.organizationConfig.showManualShipment
})

const stuartProvider = computed(() => {
  if (!shipmentProviders.value || !shipmentProviders.value.providers) return null
  return shipmentProviders.value.providers.find(p => p.name.toUpperCase() === 'STUART')
})

const ownFleetAllowed = computed<boolean>(() => {
  if (!stuartProvider.value) return false
  return !!stuartProvider.value.config?.metadata?.fleetId
})

const shouldShowOwnFleetSwitch = computed<boolean>(() => {
  return selectedShipmentProvider.value?.toUpperCase() === 'STUART' && ownFleetAllowed.value
})

const ownFleetEnabled = computed<boolean>(() => {
  if (!stuartProvider.value) return false
  return !!stuartProvider.value.config?.metadata?.ownFleetEnabled
})

const isManualShipment = computed({
  get: () => !!config.value.manualShipment,
  set: (value: boolean) => updateManualShipment(value)
})

function updateOwnFleet(ownFleetEnabled?: boolean) {
  updateShipmentProvider({
    provider: selectedShipmentProvider.value,
    ownFleetEnabled: !!ownFleetEnabled
  })
}

async function closeDelivery() {
  if (await performActionAskPinIfNeeded('DELIVERY_MANAGER')) {
    emit('close')
    router.push({ name: 'closeDelivery' })
  }
}

async function closeBookings() {
  if (!(await performActionAskPinIfNeeded('DELIVERY_MANAGER'))) return

  if (bookingsBrand.value?.closedUntil) {
    openBookings()
  } else {
    showBookingsModal.value = true
  }
}

function openBookings() {
  if (!bookingsBrand.value) return

  openDeliveries({
    [bookingsBrand.value.id]: ['Bookings']
  })

  emit('close')

  toast({
    type: 'success',
    title: t('close-bookings.bookings-opened'),
    message: t('close-bookings.bookings-opened-description')
  })
}

function handleCloseBookings(closingTime: ClosingTime) {
  if (!bookingsBrand.value) return

  closeDeliveries(
    {
      [bookingsBrand.value.id]: ['Bookings']
    },
    closingTime
  )

  showBookingsModal.value = false

  emit('close')

  toast({
    type: 'success',
    title: t('close-bookings.bookings-closed'),
    message: t('close-bookings.bookings-closed-description')
  })
}

async function routerPrompt(privilege: string, route: string) {
  if (await performActionAskPinIfNeeded(privilege)) {
    router.push({ name: route })
    emit('close')
  }
}

async function performActionAskPinIfNeeded(privilege: string) {
  const hasPrivilege = await managerConfirmation(privilege)
  if (hasPrivilege) {
    return true
  } else {
    emit('close')
    return false
  }
}

async function closeDay() {
  if (!(await performActionAskPinIfNeeded('SHIFT_MANAGER'))) return
  router.push({ name: 'endShift' })
  emit('close')
}

async function openBackoffice() {
  if (!(await performActionAskPinIfNeeded('TILL_MANAGER'))) return
  await CashMachine.openBackoffice()
}

async function openCashDrawer() {
  if (await performActionAskPinIfNeeded('CASH_DRAWER')) {
    Printer.openCashDrawer()
  }
}

function openFeedback() {
  const supportedProductFruitsLocales = ['en', 'es', 'de']
  if (!currentEmployee.value?.id) return

  let userLocale = locale?.value?.split('-')[0] || 'en'
  if (!supportedProductFruitsLocales.includes(userLocale)) {
    userLocale = 'en'
  }

  productFruits.init('7DHT4bWyeQ0HkQVx', userLocale, {
    username: currentEmployee.value.name,
    props: {
      employeeId: currentEmployee.value.id,
      locationId: locationId.value!,
      deviceId: device.value.id
    }
  })

  productFruits.safeExec(() => {
    window.productFruits?.api.surveys.startSurvey('582619db-963a-453a-9800-8cc61175f6b0')
  })

  emit('close')
}
</script>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
}

.menu-enter-active .sidebar,
.menu-leave-active .sidebar {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-enter-from .sidebar,
.menu-leave-to .sidebar {
  transform: translateX(100%);
}

.menu-left-enter-active,
.menu-left-leave-active,
.menu-right-enter-active,
.menu-right-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-left-enter-to {
  transform: translateX(0);
}

.menu-left-leave-from {
  transform: translateX(0);
}

.menu-left-enter-from {
  transform: translateX(100%);
}

.menu-left-leave-to {
  transform: translateX(-100%);
}

.menu-right-enter-to {
  transform: translateX(0);
}

.menu-right-leave-from {
  transform: translateX(0);
}

.menu-right-enter-from {
  transform: translateX(-100%);
}

.menu-right-leave-to {
  transform: translateX(100%);
}
</style>
